import { Container } from "@pixi/display";
import { Graphics, IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { DiscountResourcePackage } from "../resources/DiscountResourcePackage";
import { DiscountCouponState } from "./DiscountCouponState";
import { StringFormatExtention } from "by/gamefactory/extensions/StringFormatExtention";
import { IUseDiscountViewModel } from "../../usediscount/model/IUseDiscountViewModel";
import { Event } from "event-system";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { CheckBox } from "@pixi/ui";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { BenefitItem } from "by/gamefactory/cityversetycoon/scenes/common/models/benefits/BenefitItem";

export class DiscountCouponContainer extends Container {
    private readonly discountCouponState: DiscountCouponState;
    private readonly benefitDynamicResources: DynamicResourcesPackage;
    private readonly discountResources: DiscountResourcePackage;
    private readonly benefitItem: BenefitItem;
    private readonly useDiscountVIewModel: IUseDiscountViewModel;

    private frame: Sprite;
    private discountIcon: Sprite;
    private percentText: Text;
    private additionalText: Text;
    private expiresText: Text;
    private daysCountText: Text;
    private backlight: Sprite;
    private useCheckBox: CheckBox;

    public readonly onCheckBoxUp: Event<void>;
    public readonly onCheckBoxDown: Event<void>;
    public readonly onCheckBoxOut: Event<void>;

    public constructor(
        benefitResources: DynamicResourcesPackage,
        discountResources: DiscountResourcePackage,
        discountCouponState: DiscountCouponState,
        benefitItem: BenefitItem,
        useDiscountVIewModel?: IUseDiscountViewModel
    ) {
        super();
        this.benefitDynamicResources = benefitResources;
        this.discountResources = discountResources;
        this.discountCouponState = discountCouponState;
        this.benefitItem = benefitItem;
        this.useDiscountVIewModel = useDiscountVIewModel;
        this.onCheckBoxUp = new Event();
        this.onCheckBoxDown = new Event();
        this.onCheckBoxOut = new Event();
        this.build();
    }

    private build(): void {
        this.frame = new Sprite(this.discountResources.assets_views_discount_discount_frame_png);
        this.addChild(this.frame);
        const maskGraphic = new Graphics();
        maskGraphic.beginFill(0x000000);
        maskGraphic.drawRoundedRect(0, 0, this.frame.width, this.frame.height, 32);
        maskGraphic.endFill();
        this.addChild(maskGraphic);
        this.frame.mask = maskGraphic;
        this.discountIcon = new Sprite(this.benefitDynamicResources.resource(this.benefitItem.imageUrl));
        this.discountIcon.width = 256;
        this.discountIcon.height = 244;
        this.discountIcon.mask = maskGraphic;
        this.discountIcon.position.set(-52.51, 13);
        this.frame.addChild(this.discountIcon);

        const expiresTextValue = StringFormatExtention.expiresFromMsFormat(this.benefitItem.endsIn ?? 0);

        this.percentText = new Text(`${this.benefitItem.buildingPurchaseCouponDiscountPercentage}%`, new DiscountTextStyle());
        this.percentText.style.fill = ["#87f20c", "#2cd613"];
        this.percentText.position.set(243, this.benefitItem.endsIn > 0 ? 55 : 90);
        this.frame.addChild(this.percentText);
        const couponText = this.benefitItem.endsIn > 0 ? "DISCOUNT COUPON," : "DISCOUNT COUPON";
        this.additionalText = new Text(couponText, new DiscountTextStyle());
        this.additionalText.position.set(this.percentText.x + this.percentText.width + 7, this.percentText.y);
        this.frame.addChild(this.additionalText);

        this.expiresText = new Text(`EXPIRES IN`, new DiscountTextStyle());
        this.expiresText.position.set(this.percentText.x, 116);
        this.expiresText.visible = this.benefitItem.endsIn > 0;
        this.frame.addChild(this.expiresText);
        this.daysCountText = new Text(expiresTextValue, new DiscountTextStyle());
        this.daysCountText.style.fill = ["#ff4e4b", "#ff2982", "#ff01bc"];
        this.daysCountText.position.set(this.expiresText.x + this.expiresText.width + 7, this.expiresText.y);
        this.daysCountText.visible = this.benefitItem.endsIn > 0;
        this.frame.addChild(this.daysCountText);

        if (this.discountCouponState === DiscountCouponState.USED) {
            this.useCheckBox = new CheckBox({
                checked: false,
                style: {
                    unchecked: new Sprite(this.discountResources.assets_views_discount_uncheck_png),
                    checked: new Sprite(this.discountResources.assets_views_discount_check_png)
                }
            });
            this.useCheckBox.position.set(659, 80);
            this.onCheckBoxStateChange = this.onCheckBoxStateChange.bind(this);
            this.useCheckBox.onCheck.connect(this.onCheckBoxStateChange);
            this.addChild(this.useCheckBox);
            this.useCheckBox.innerView.on("pointerup", this.onCheckboxUpHandle, this);
            this.useCheckBox.innerView.on("pointerdown", this.onCheckboxDownHandle, this);
            this.useCheckBox.innerView.on("pointerout", this.onCheckboxOutHandle, this);
        }
        this.backlight = new Sprite(this.discountResources.assets_views_discount_discount_light_png);
        this.backlight.position.set(this.frame.x + this.frame.width * 0.5 - this.backlight.width * 0.5, this.frame.y + this.frame.height);
        this.addChild(this.backlight);
    }

    private onCheckBoxStateChange(selected: boolean): void {
        const summaryDiscountPercentage = this.useDiscountVIewModel.summaryDiscountPercentage.value;
        const appliedCoupons = this.useDiscountVIewModel.appliedCoupons.value;
        if (selected) {
            appliedCoupons.push(this.benefitItem.id);
            this.useDiscountVIewModel.summaryDiscountPercentage.setValue(summaryDiscountPercentage - (this.benefitItem.buildingPurchaseCouponDiscountPercentage / 100));
        } else {
            appliedCoupons.splice(appliedCoupons.indexOf(this.benefitItem.id), 1);
            this.useDiscountVIewModel.summaryDiscountPercentage.setValue(summaryDiscountPercentage + (this.benefitItem.buildingPurchaseCouponDiscountPercentage / 100));
        }
    }

    private onCheckboxUpHandle(): void {
        this.onCheckBoxUp.emit();
    }

    private onCheckboxDownHandle(): void {
        this.onCheckBoxDown.emit();
    }

    private onCheckboxOutHandle(): void {
        this.onCheckBoxOut.emit();
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        if (this.discountCouponState === DiscountCouponState.USED) {
            this.useCheckBox.onCheck.disconnect(this.onCheckBoxStateChange);
            this.useCheckBox.innerView.off("pointerup", this.onCheckboxUpHandle, this);
            this.useCheckBox.innerView.off("pointerdown", this.onCheckboxDownHandle, this);
            this.useCheckBox.innerView.off("pointerout", this.onCheckboxOutHandle, this);
        }
        super.destroy(options);
    }
}

class DiscountTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48
        }));
    }
}
